import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API } from "@/App";

/**
 * Hook to translate DB content (descriptions, bios) to the user's selected language.
 * Caches translations in MongoDB via the /api/translate endpoint.
 * Returns original text instantly, then replaces with translation when ready.
 */
export function useTranslateContent(text, cacheKey) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.split("-")[0] || "en";
  const [translated, setTranslated] = useState(text);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!text || lang === "en") {
      setTranslated(text);
      return;
    }

    let cancelled = false;
    setLoading(true);

    axios.post(`${API}/translate`, {
      text,
      lang,
      cache_key: cacheKey || "",
    }).then(res => {
      if (!cancelled) setTranslated(res.data.translated);
    }).catch(() => {
      if (!cancelled) setTranslated(text);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [text, lang, cacheKey]);

  return { translated, loading, isTranslated: lang !== "en" };
}

/**
 * Hook to batch-translate multiple texts.
 * Pass an array of {text, cacheKey} objects.
 */
export function useTranslateBatch(items) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.split("-")[0] || "en";
  const [results, setResults] = useState(items.map(i => i.text));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (lang === "en" || !items.length) {
      setResults(items.map(i => i.text));
      return;
    }

    let cancelled = false;
    setLoading(true);

    axios.post(`${API}/translate/batch`, {
      items: items.map(i => ({ text: i.text, cache_key: i.cacheKey || "" })),
      lang,
    }).then(res => {
      if (!cancelled) setResults(res.data.results.map(r => r.text));
    }).catch(() => {
      if (!cancelled) setResults(items.map(i => i.text));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [lang, JSON.stringify(items.map(i => i.cacheKey))]);

  return { results, loading };
}
